import { useState, useMemo, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useRecovery } from '../context/RecoveryContext';
import { useSafe } from '../context/SafeContext';
import { useAccountKey } from '../context/AccountKeyContext';
import { useEntitlement } from '../hooks/useEntitlement';
import { ProBadge } from '../components/ProBadge';
import { ProModal } from '../components/ProModal';
import {
  wrapForRecoveryContact,
  unwrapForRecoveryContact,
  wrapForResetEphemeral,
  safetyWords,
  ephemeralWords,
  EMERGENCY_WAIT_PRESETS_HOURS,
  EMERGENCY_WAIT_DEFAULT_HOURS,
} from '@heybesafe/shared';
import type { RecoveryContactSummary, RecoveryBundle, OrgMember, SignedKey, InboundRecoveryRelationship } from '@heybesafe/shared';
import { base64UrlToBytes } from '../crypto/base64url';
import { pinIdentity, markIdentityVerified, isIdentityVerified } from '../pkastore/pinnedIdentities';
import { accountKeyAuthentic as contactKeyAuthentic } from '../pkastore/accountKeyAuth';
import { getOrg, getCurrentUserId, notifyRecoveryVerification } from '../api/client';

/** Group a 60-digit safety number into 5-digit blocks for readability. */
/** A numbered step heading. The designate flow is a sequence — pick, verify,
 *  then choose a window — and presenting it as an undifferentiated form was
 *  the whole reason it read as confusing. */
function StepHeading({ n, children }: { n: number; children: React.ReactNode }): JSX.Element {
  return (
    <h3 className="text-sm font-semibold text-hbs-text mb-2 flex items-center gap-2">
      <span className="flex items-center justify-center w-5 h-5 rounded-full bg-hbs-primary text-hbs-on-primary text-xs shrink-0">
        {n}
      </span>
      {children}
    </h3>
  );
}

/**
 * The six safety words, numbered, in a two-column grid. Numbered because the
 * two people are reading them to each other and need to stay in step, and
 * because "third word" is how they will actually refer to one out loud.
 *
 * Column-major (`grid-rows-3 grid-flow-col`): 1-3 down the left, 4-6 down the
 * right, so reading straight down a column gives the right order. Row-major
 * would put 1,2 side by side and invite one person to read across while the
 * other reads down.
 */
function SafetyWordList({ words, testId }: { words: string[] | null; testId: string }): JSX.Element {
  if (!words) {
    return <div className="text-sm text-hbs-text-mute p-2" data-testid={testId}>Computing…</div>;
  }
  return (
    <ol
      className="grid grid-rows-3 grid-flow-col gap-x-6 gap-y-1 bg-hbs-surface rounded p-3 mb-2 max-w-sm"
      data-testid={testId}
    >
      {words.map((w, i) => (
        <li key={w} className="flex items-baseline gap-2">
          <span className="text-xs text-hbs-text-mute tabular-nums w-4 shrink-0">{i + 1}.</span>
          <span className="font-mono text-sm text-hbs-text">{w}</span>
        </li>
      ))}
    </ol>
  );
}

/** "24 hours" / "3 days" / "7 days" / "30 days". Used on the designate
 *  picker, the per-contact summary row, and the inbound confirm dialog so
 *  the user sees the same phrasing wherever the wait period is mentioned.
 *
 *  24h renders as "24 hours" rather than "1 day" — in an emergency-recovery
 *  context the hour count feels more responsive and concrete than a day
 *  count for the shortest preset. Above 24h we use day formatting. */
export function formatWaitPeriod(hours: number): string {
  if (hours <= 24 || hours % 24 !== 0) return `${hours} hours`;
  const days = hours / 24;
  return `${days} days`;
}

function StateBadge({ state }: { state: RecoveryContactSummary['state'] }): JSX.Element {
  const map: Record<RecoveryContactSummary['state'], string> = {
    PENDING_ACCEPT: 'bg-amber-500/20 text-amber-400',
    ACTIVE: 'bg-emerald-500/20 text-emerald-400',
    RESET_REQUESTED: 'bg-sky-500/20 text-sky-400',
    RESET_APPROVED: 'bg-sky-500/20 text-sky-400',
    EMERGENCY_REQUESTED: 'bg-red-500/20 text-red-400',
  };
  return (
    <span className={`text-xs px-2 py-0.5 rounded font-medium ${map[state]}`}>
      {state.replace(/_/g, ' ').toLowerCase()}
    </span>
  );
}

export default function RecoveryContactsPage(): JSX.Element {
  const {
    outbound, inbound, refresh, removeContact, cancelReset,
    acceptDesignation, declineDesignation, requestEmergencyAccess,
  } = useRecovery();
  const [showAdd, setShowAdd] = useState(false);
  const { canUseRecovery } = useEntitlement();
  const [proModalOpen, setProModalOpen] = useState(false);

  // RecoveryProvider sits high in the tree and only fetches on initial mount —
  // re-fetch whenever the user opens this page (and again on tab focus) so the
  // inbound/outbound lists reflect what the other party has done since.
  useEffect(() => {
    void refresh();
    const onFocus = () => { void refresh(); };
    window.addEventListener('focus', onFocus);
    return () => { window.removeEventListener('focus', onFocus); };
  }, [refresh]);

  return (
    <div className="max-w-3xl mx-auto p-6">
      <Link to="/orgs" className="text-sm text-hbs-text-mute hover:text-hbs-text inline-flex items-center gap-1 mb-4">
        ← Back to Families
      </Link>
      <header className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-display font-semibold text-hbs-text">Recovery contacts</h1>
          <p className="text-sm text-hbs-text-mute mt-1">
            People who can help you recover your safe if you forget your password,
            and request emergency access if you're unreachable.
          </p>
          <p className="text-sm text-hbs-text-mute mt-1">
            When one of your recovery contacts requests emergency access, you have
            a time window where you can reject the request.
          </p>
        </div>
        <button
          type="button"
          id="add-recovery-contact-button"
          onClick={canUseRecovery ? () => setShowAdd(true) : () => setProModalOpen(true)}
          className="bg-hbs-primary text-hbs-on-primary px-4 py-2 rounded font-semibold text-sm"
        >
          Add recovery contact
          {!canUseRecovery && <ProBadge />}
        </button>
      </header>

      <section>
        <h2 className="text-sm font-semibold uppercase tracking-wide text-hbs-text-mute mb-2">
          Your contacts ({outbound.length})
        </h2>
        {outbound.length === 0 ? (
          <p className="text-sm text-hbs-text-mute py-8 text-center">
            You haven't designated any recovery contacts yet.
          </p>
        ) : (
          <ul className="flex flex-col gap-2">
            {outbound.map((c) => (
              <OutboundRow key={c.contactUserId} contact={c} onRemove={removeContact} onCancelReset={cancelReset} />
            ))}
          </ul>
        )}
      </section>

      {inbound.length > 0 && (
        <section className="mt-8">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-hbs-text-mute mb-2">
            People who chose you as their recovery contact ({inbound.length})
          </h2>
          <ul className="flex flex-col gap-2">
            {inbound.map((r) => (
              <li key={r.protectedUserId} className="p-3 bg-hbs-surface rounded border border-hbs-border">
                <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-medium text-hbs-text">{r.protectedEmail}</div>
                  <div className="mt-1 flex items-center gap-2">
                    <StateBadge state={r.state} />
                    <span className="text-xs text-hbs-text-mute">
                      wait period: {formatWaitPeriod(r.emergencyWaitHours ?? EMERGENCY_WAIT_DEFAULT_HOURS)}
                    </span>
                  </div>
                </div>
                <InboundRowActions
                  rel={r}
                  onAccept={acceptDesignation}
                  onDecline={declineDesignation}
                  onRequestEmergency={requestEmergencyAccess}
                />
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      <CompareWordsSection />

      {proModalOpen && <ProModal feature="recovery" onClose={() => setProModalOpen(false)} />}
      {showAdd && <AddContactModal onClose={() => setShowAdd(false)} />}
    </div>
  );
}

function OutboundRow({
  contact, onRemove, onCancelReset,
}: {
  contact: RecoveryContactSummary;
  onRemove: (id: string) => Promise<void>;
  onCancelReset: (id: string) => Promise<void>;
}): JSX.Element {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function handleRemove() {
    setBusy(true); setError(null);
    try { await onRemove(contact.contactUserId); }
    catch (e) { setError(e instanceof Error ? e.message : 'Failed to remove'); }
    finally { setBusy(false); }
  }
  async function handleCancelReset() {
    setBusy(true); setError(null);
    try { await onCancelReset(contact.contactUserId); }
    catch (e) { setError(e instanceof Error ? e.message : 'Failed to cancel'); }
    finally { setBusy(false); }
  }
  // A reset is in progress (requested by this account, or already approved by
  // the contact) — offer to cancel it, which returns the contact to ACTIVE.
  const resetInProgress = contact.state === 'RESET_REQUESTED' || contact.state === 'RESET_APPROVED';
  const waitHours = contact.emergencyWaitHours ?? EMERGENCY_WAIT_DEFAULT_HOURS;
  return (
    <li className="flex items-center justify-between p-3 bg-hbs-surface rounded border border-hbs-border">
      <div>
        <div className="text-sm font-medium text-hbs-text">{contact.contactEmail}</div>
        <div className="mt-1 flex items-center gap-2">
          <StateBadge state={contact.state} />
          <span className="text-xs text-hbs-text-mute">
            cancel window: {formatWaitPeriod(waitHours)}
          </span>
        </div>
        {error && <p className="text-xs text-red-400 mt-1">{error}</p>}
      </div>
      <div className="flex items-center gap-3">
        {resetInProgress && (
          <button type="button" disabled={busy} id={`recovery-cancel-reset-${contact.contactUserId}`}
          onClick={handleCancelReset}
            className="text-sm text-hbs-primary hover:underline disabled:opacity-50">
            {busy ? 'Cancelling…' : 'Cancel reset'}
          </button>
        )}
        <button type="button" disabled={busy} id={`recovery-remove-${contact.contactUserId}`}
          onClick={handleRemove}
          className="text-sm text-hbs-text-mute hover:text-red-400 disabled:opacity-50">
          {busy ? 'Removing…' : 'Remove'}
        </button>
      </div>
    </li>
  );
}

// ─── Inbound row helpers ──────────────────────────────────────────────────────

function daysUntil(iso: string): number {
  const diffMs = new Date(iso).getTime() - Date.now();
  return Math.max(0, Math.ceil(diffMs / (24 * 60 * 60 * 1000)));
}

function InboundRowActions({
  rel,
  onAccept, onDecline, onRequestEmergency,
}: {
  rel: InboundRecoveryRelationship;
  onAccept: (id: string) => Promise<void>;
  onDecline: (id: string) => Promise<void>;
  onRequestEmergency: (id: string) => Promise<void>;
}): JSX.Element | null {
  const [confirmEmergency, setConfirmEmergency] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function run(fn: () => Promise<void>) {
    setBusy(true); setError(null);
    try { await fn(); }
    catch (e) { setError(e instanceof Error ? e.message : 'Action failed'); }
    finally { setBusy(false); }
  }

  if (rel.state === 'PENDING_ACCEPT') {
    return (
      <div className="flex flex-col items-end gap-1">
        <div className="flex gap-2">
          <button type="button" disabled={busy} id={`recovery-accept-${rel.protectedUserId}`}
        onClick={() => void run(() => onAccept(rel.protectedUserId))}
            className="bg-hbs-primary text-hbs-on-primary px-3 py-2 rounded font-semibold text-sm disabled:opacity-50">
            {busy ? 'Working…' : 'Accept'}
          </button>
          <button type="button" disabled={busy} id={`recovery-decline-${rel.protectedUserId}`}
        onClick={() => void run(() => onDecline(rel.protectedUserId))}
            className="text-sm text-hbs-text-mute hover:text-red-400 px-3 py-2 disabled:opacity-50">Decline</button>
        </div>
        {error && <p className="text-xs text-red-400">{error}</p>}
      </div>
    );
  }
  if (rel.state === 'ACTIVE') {
    return (
      <div className="flex flex-col items-end gap-1">
        <button type="button" disabled={busy} id={`recovery-request-emergency-${rel.protectedUserId}`}
        onClick={() => setConfirmEmergency(true)}
          className="text-sm text-amber-300 border border-amber-500/40 rounded px-3 py-2 hover:bg-amber-500/10 disabled:opacity-50">
          Request emergency access
        </button>
        {error && <p className="text-xs text-red-400">{error}</p>}
        {confirmEmergency && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" role="dialog">
            <div className="bg-hbs-surface rounded p-6 max-w-md">
              <h3 className="text-base font-semibold mb-2">Are you sure?</h3>
              <p className="text-sm text-hbs-text-mute mb-4">
                {rel.protectedEmail} will be notified immediately. If they don't cancel within{' '}
                {formatWaitPeriod(rel.emergencyWaitHours ?? EMERGENCY_WAIT_DEFAULT_HOURS)},
                you'll get read-only access to their safe.
              </p>
              <div className="flex gap-2 justify-end">
                <button type="button" id={`recovery-emergency-cancel-${rel.protectedUserId}`}
            onClick={() => setConfirmEmergency(false)}
                  className="px-3 py-2 text-sm text-hbs-text-mute">Cancel</button>
                <button type="button"
                  id={`recovery-emergency-confirm-${rel.protectedUserId}`}
            onClick={() => { setConfirmEmergency(false); void run(() => onRequestEmergency(rel.protectedUserId)); }}
                  className="bg-amber-500 text-black px-3 py-2 rounded font-semibold text-sm">Request</button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }
  if (rel.state === 'EMERGENCY_REQUESTED') {
    if (!rel.emergencyReleaseAt) return null;
    const released = new Date(rel.emergencyReleaseAt) <= new Date();
    if (released) {
      return (
        <Link to={`/safe/recovery/${rel.protectedUserId}`}
          className="bg-hbs-primary text-hbs-on-primary px-3 py-2 rounded font-semibold text-sm">
          View safe
        </Link>
      );
    }
    return (
      <span className="text-sm text-amber-300">
        {daysUntil(rel.emergencyReleaseAt)} days until access
      </span>
    );
  }
  if (rel.state === 'RESET_REQUESTED') {
    return <ResetRequestActions rel={rel} />;
  }
  return null;
}

function ResetRequestActions({ rel }: { rel: InboundRecoveryRelationship }): JSX.Element {
  const { approveReset, denyReset, fetchResetPackage } = useRecovery();
  const { privateKey: accountPrivateKey } = useAccountKey();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Layer 2b: the requester's app shows a 6-digit code they must read to this
  // contact out-of-band; the contact types it here and it's checked server-side
  // on approve. Approve stays disabled until 6 digits are entered.
  const [code, setCode] = useState('');
  const codeDigits = code.replace(/\D/g, '').slice(0, 6);
  const codeComplete = codeDigits.length === 6;
  // PKA A5: the reset requester is locked out of their identity key mid-reset, so
  // the ephemeral can't be identity-signed. Instead the contact confirms a
  // fingerprint of the SERVED ephemeral matches what the requester reads them
  // out-of-band — catching a server that substitutes the ephemeral. We fetch the
  // package once here, show its fingerprint, and wrap to that same package.
  const [resetPkg, setResetPkg] = useState<Awaited<ReturnType<typeof fetchResetPackage>> | null>(null);
  const [ephFp, setEphFp] = useState<string[] | null>(null);
  const [fpConfirmed, setFpConfirmed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const pkg = await fetchResetPackage(rel.protectedUserId);
        if (cancelled) return;
        setResetPkg(pkg);
        const fp = await ephemeralWords(base64UrlToBytes(pkg.resetEphemeralPub));
        if (!cancelled) setEphFp(fp);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load reset request');
      }
    })();
    return () => { cancelled = true; };
  }, [rel.protectedUserId, fetchResetPackage]);

  async function handleDeny() {
    setBusy(true); setError(null);
    try { await denyReset(rel.protectedUserId); }
    catch (e) { setError(e instanceof Error ? e.message : 'Failed to deny'); }
    finally { setBusy(false); }
  }

  async function handleApprove() {
    setBusy(true); setError(null);
    try {
      // 1. Use the package fetched at mount (the one whose fingerprint the
      //    contact confirmed) — do NOT re-fetch, so we can't wrap to a
      //    different ephemeral than the one that was verified out-of-band.
      const pkg = resetPkg;
      if (!pkg) throw new Error('Reset request not loaded yet — try again.');
      if (!fpConfirmed) throw new Error('Confirm the security number with the requester first.');
      // 2. Decrypt the contact's stored wraps with B's account private key.
      const vaultBundle = JSON.parse(atob(pkg.wrappedVaultKey));
      const accountBundle = JSON.parse(atob(pkg.wrappedAccountPrivateKey));
      const vaultKey = await unwrapForRecoveryContact(
        vaultBundle, accountPrivateKey!, 'recovery-vault-key-v1',
      );
      const accountKey = await unwrapForRecoveryContact(
        accountBundle, accountPrivateKey!, 'recovery-account-key-v1',
      );
      // 3. Re-wrap to A's ephemeral pub.
      const ephPub = base64UrlToBytes(pkg.resetEphemeralPub);
      const resetVaultBundle = await wrapForResetEphemeral(
        vaultKey, ephPub, 'recovery-reset-vault-key-v1',
      );
      const resetAccountBundle = await wrapForResetEphemeral(
        accountKey, ephPub, 'recovery-reset-account-key-v1',
      );
      // 4. POST approve (with the verification code the requester read out-of-band).
      await approveReset(rel.protectedUserId, {
        resetWrappedVaultKey: btoa(JSON.stringify(resetVaultBundle)),
        resetWrappedAccountPrivateKey: btoa(JSON.stringify(resetAccountBundle)),
        verificationCode: codeDigits,
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to approve');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex flex-col items-end gap-2 max-w-md">
      <p className="text-xs text-hbs-primary bg-amber-500/10 border border-amber-500/40 rounded px-3 py-2 text-left">
        Before approving, confirm with {rel.protectedEmail} by phone or in person
        that they actually requested this reset. Ask them to read you the 6-digit
        code shown in their app, and enter it below. Approval lets them set a new
        password and access their safe.
      </p>
      <div className="flex flex-col items-end gap-1 w-full">
        <label className="text-xs text-hbs-text-mute self-start" htmlFor={`vcode-${rel.protectedUserId}`}>
          6-digit code from {rel.protectedEmail}
        </label>
        <input
          id={`vcode-${rel.protectedUserId}`}
          type="text"
          inputMode="numeric"
          autoComplete="off"
          value={codeDigits}
          onChange={(e) => setCode(e.target.value)}
          placeholder="000000"
          className="w-full font-mono tracking-hbs-mono text-lg text-hbs-text bg-hbs-surface-alt border border-hbs-border rounded px-3 py-2"
        />
      </div>
      <div className="flex flex-col items-start gap-1 w-full">
        <label className="text-xs text-hbs-text-mute" htmlFor={`ephfp-${rel.protectedUserId}`}>
          Security words — {rel.protectedEmail} should read you these same six words
        </label>
        <SafetyWordList words={ephFp} testId={`reset-fingerprint-${rel.protectedUserId}`} />
        <label className="text-xs text-hbs-text flex items-start gap-2">
          <input
            id={`ephfp-${rel.protectedUserId}`}
            type="checkbox"
            checked={fpConfirmed}
            disabled={!ephFp}
            onChange={(e) => setFpConfirmed(e.target.checked)}
            className="mt-0.5"
          />
          <span>{rel.protectedEmail} read me these exact words out-of-band.</span>
        </label>
      </div>
      <div className="flex gap-2">
        <button type="button" disabled={busy || !codeComplete || !fpConfirmed || !ephFp}
          id={`recovery-approve-reset-${rel.protectedUserId}`}
        onClick={handleApprove}
          className="bg-hbs-primary text-hbs-on-primary px-3 py-2 rounded font-semibold text-sm disabled:opacity-50">
          {busy ? 'Approving…' : 'Approve'}
        </button>
        <button type="button" disabled={busy}
          id={`recovery-deny-reset-${rel.protectedUserId}`}
        onClick={handleDeny}
          className="text-sm text-hbs-text-mute hover:text-red-400 px-3 py-2 disabled:opacity-50">Deny</button>
      </div>
      {error && <p className="text-xs text-red-400">{error}</p>}
    </div>
  );
}

// ─── Candidate member entry (loaded per-org at modal open time) ───────────────

/**
 * Every family member except the caller, across all their families, with the
 * PKA fields needed to compute pairwise safety words.
 *
 * Shared by the designate modal and the standalone compare view so the two can
 * never disagree about who is verifiable.
 */
function useFamilyMembers(): { members: MemberCandidate[]; loading: boolean } {
  const { orgs } = useSafe();
  const [members, setMembers] = useState<MemberCandidate[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      const all: MemberCandidate[] = [];
      const seen = new Set<string>();
      const me = getCurrentUserId();
      for (const org of orgs) {
        const result = await getOrg(org.id);
        if (cancelled) return;
        if (!result.ok) continue;
        for (const m of result.data.members as OrgMember[]) {
          if (m.userId === me || seen.has(m.userId)) continue;
          seen.add(m.userId);
          all.push({
            userId: m.userId, email: m.email, familyName: org.name,
            accountPublicKey: m.accountPublicKey,
            identityPublicKey: m.identityPublicKey,
            accountPublicKeySig: m.accountPublicKeySig,
          });
        }
      }
      if (!cancelled) { setMembers(all); setLoading(false); }
    }
    void load();
    return () => { cancelled = true; };
  }, [orgs]);

  return { members, loading };
}

/**
 * Look up the six safety words for any family member, with no designation
 * involved.
 *
 * This exists because the designate flow is otherwise circular: the designator
 * must compare words BEFORE wrapping their vault key (an unverified key is
 * exactly what the wrap must not go to), but the contact's inbound row — and
 * so their copy of the words — does not exist until the designation is already
 * created. Without a way to look the words up on demand, the person being read
 * to has nothing to read back, and the comparison cannot happen at all.
 */
function CompareWordsSection(): JSX.Element {
  const { identityPublicKey } = useSafe();
  const { members, loading } = useFamilyMembers();
  const [pickedId, setPickedId] = useState<string | null>(null);
  const [words, setWords] = useState<string[] | null>(null);

  const picked = members.find((m) => m.userId === pickedId);

  useEffect(() => {
    setWords(null);
    const me = getCurrentUserId();
    if (!picked?.identityPublicKey || !identityPublicKey || !me) return;
    let cancelled = false;
    void safetyWords(
      identityPublicKey, me,
      base64UrlToBytes(picked.identityPublicKey), picked.userId,
    ).then((w) => { if (!cancelled) setWords(w); });
    return () => { cancelled = true; };
  }, [picked, identityPublicKey]);

  return (
    <section className="mt-8" id="compare-security-words">
      <h2 className="text-sm font-semibold uppercase tracking-wide text-hbs-text-mute mb-2">
        Compare security words
      </h2>
      <p className="text-sm text-hbs-text-mute mb-3">
        Pick a family member to see the six words you share with them. Read them to
        each other <strong className="font-semibold text-hbs-text">in person or on a
        call</strong> — you should both see the same six words in the same order. Use
        this when someone is adding you as their recovery contact.
      </p>
      {loading ? (
        <p className="text-sm text-hbs-text-mute">Loading family members…</p>
      ) : members.length === 0 ? (
        <p className="text-sm text-hbs-text-mute">No family members to compare with yet.</p>
      ) : (
        <>
          <label className="text-xs text-hbs-text-mute block mb-1" htmlFor="compare-words-picker">
            Family member
          </label>
          <select
            id="compare-words-picker"
            className="w-full p-2 rounded border border-hbs-border bg-hbs-surface text-sm text-hbs-text mb-3"
            value={pickedId ?? ''}
            onChange={(e) => setPickedId(e.target.value || null)}
          >
            <option value="">Choose someone…</option>
            {members.map((m) => (
              <option key={m.userId} value={m.userId}>{m.email} — {m.familyName}</option>
            ))}
          </select>
          {picked && (
            picked.identityPublicKey ? (
              <SafetyWordList words={words} testId="compare-safety-words" />
            ) : (
              <p className="text-sm text-hbs-text-mute">
                {picked.email} hasn't set up security verification yet. Ask them to sign in
                to the latest version of Hey Be Safe.
              </p>
            )
          )}
        </>
      )}
    </section>
  );
}

interface MemberCandidate {
  userId: string;
  email: string;
  familyName: string;
  accountPublicKey?: string;
  identityPublicKey?: string;
  accountPublicKeySig?: SignedKey;
}

// ─── Serialise a RecoveryBundle as base64(JSON) ───────────────────────────────

function serializeBundle(b: RecoveryBundle): string {
  return btoa(JSON.stringify(b));
}

// ─── AddContactModal ──────────────────────────────────────────────────────────

function AddContactModal({ onClose }: { onClose: () => void }): JSX.Element {
  const { outbound, designate } = useRecovery();
  const { orgs, vaultKey, identityPublicKey } = useSafe();
  const { privateKey: accountPrivateKey } = useAccountKey();

  const [candidates, setCandidates] = useState<MemberCandidate[]>([]);
  const [loadingMembers, setLoadingMembers] = useState(true);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [waitHours, setWaitHours] = useState<number>(EMERGENCY_WAIT_DEFAULT_HOURS);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // PKA verification (L3): the pairwise safety number for the selected contact,
  // and a tick to re-render after the user marks them verified.
  const [safetyWordList, setSafetyWordList] = useState<string[] | null>(null);
  const [, setVerifiedTick] = useState(0);

  const selected = candidates.find((c) => c.userId === selectedUserId);
  const selectedVerified = !!selected?.identityPublicKey
    && isIdentityVerified(selected.userId, selected.identityPublicKey);

  const existingContactIds = useMemo(
    () => new Set(outbound.map((o) => o.contactUserId)),
    [outbound],
  );

  // Fetch org member rosters (with accountPublicKey) at modal-open time.
  // GET /orgs/:id returns { org, members } with accountPublicKey projected from UserRecord.
  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoadingMembers(true);
      const allCandidates: MemberCandidate[] = [];
      const seen = new Set<string>();
      const currentUserId = getCurrentUserId();

      for (const org of orgs) {
        const result = await getOrg(org.id);
        if (cancelled) return;
        if (!result.ok) continue;

        const members: OrgMember[] = result.data.members;
        for (const m of members) {
          if (m.userId === currentUserId) continue;
          if (existingContactIds.has(m.userId)) continue;
          if (seen.has(m.userId)) continue;
          seen.add(m.userId);
          allCandidates.push({
            userId: m.userId,
            email: m.email,
            familyName: org.name,
            accountPublicKey: m.accountPublicKey,
            identityPublicKey: m.identityPublicKey,
            accountPublicKeySig: m.accountPublicKeySig,
          });
        }
      }

      if (!cancelled) {
        setCandidates(allCandidates);
        setLoadingMembers(false);
      }
    }
    void load();
    return () => { cancelled = true; };
  }, [orgs, existingContactIds]);

  // Reaching the verify step is what triggers the contact's email — they need
  // the "compare security words" instructions NOW, while the designator is
  // waiting to compare, not after Confirm creates the designation. Fired once
  // per contact per mount; the server also rate-limits per pair, so flipping
  // between candidates in the picker doesn't spray mail.
  const notifiedRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!selected?.identityPublicKey || selectedVerified) return;
    if (notifiedRef.current.has(selected.userId)) return;
    notifiedRef.current.add(selected.userId);
    // Best-effort: a failed notification must not block the designator, who can
    // still read the words out over the phone without it.
    void notifyRecoveryVerification(selected.userId).catch(() => {});
  }, [selected, selectedVerified]);

  // Compute the pairwise safety number for the selected contact so the user can
  // compare it with them out-of-band before verifying.
  useEffect(() => {
    setSafetyWordList(null);
    if (!selected?.identityPublicKey || !identityPublicKey) return;
    const myId = getCurrentUserId();
    if (!myId) return;
    let cancelled = false;
    void safetyWords(identityPublicKey, myId, base64UrlToBytes(selected.identityPublicKey), selected.userId)
      .then((w) => { if (!cancelled) setSafetyWordList(w); });
    return () => { cancelled = true; };
  }, [selected, identityPublicKey]);

  // Verify the contact's key (PKA): authenticate the account-key signature, pin
  // the identity (TOFU; block a changed key), then mark it verified.
  function handleVerify() {
    setError(null);
    if (!selected) return;
    if (!contactKeyAuthentic(selected)) {
      setError("Could not verify this contact's security key — do not proceed. Ask them to sign in to the latest version.");
      return;
    }
    const { changed } = pinIdentity(selected.userId, selected.identityPublicKey!);
    if (changed) {
      setError("This contact's security key changed since you last verified it. Re-verify with them in person or over a call before continuing.");
      return;
    }
    markIdentityVerified(selected.userId);
    setVerifiedTick((t) => t + 1);
  }

  async function handleConfirm() {
    if (!selectedUserId) return;
    setSubmitting(true);
    setError(null);
    try {
      if (!vaultKey) throw new Error('Safe is locked — unlock your safe first.');
      if (!accountPrivateKey) throw new Error('Account key not available — please sign in again.');

      const candidate = candidates.find((c) => c.userId === selectedUserId);
      if (!candidate?.accountPublicKey) {
        throw new Error("Could not find the contact's account public key. Try again later.");
      }

      // PKA gate: authenticate the account key, confirm the identity is pinned +
      // unchanged + verified. Never wrap secrets to a server-supplied key that
      // hasn't cleared out-of-band verification (finding #5 / A4).
      if (!contactKeyAuthentic(candidate)) {
        throw new Error("Could not verify the contact's security key. Do not proceed.");
      }
      if (pinIdentity(candidate.userId, candidate.identityPublicKey!).changed) {
        throw new Error("This contact's security key changed — re-verify before designating.");
      }
      if (!isIdentityVerified(candidate.userId, candidate.identityPublicKey!)) {
        throw new Error("Verify this contact's security number with them before designating.");
      }

      const contactPublicKeyBytes = base64UrlToBytes(candidate.accountPublicKey);

      // Export the vault key (AES-GCM CryptoKey) to raw bytes for ECIES input.
      const vaultKeyBytes = new Uint8Array(
        await crypto.subtle.exportKey('raw', vaultKey),
      );

      const wrappedVaultBundle = await wrapForRecoveryContact(
        vaultKeyBytes,
        contactPublicKeyBytes,
        'recovery-vault-key-v1',
      );
      const wrappedAccountBundle = await wrapForRecoveryContact(
        accountPrivateKey,
        contactPublicKeyBytes,
        'recovery-account-key-v1',
      );

      await designate({
        contactUserId: selectedUserId,
        wrappedVaultKey: serializeBundle(wrappedVaultBundle),
        wrappedAccountPrivateKey: serializeBundle(wrappedAccountBundle),
        emergencyWaitHours: waitHours,
      });
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to designate contact');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" role="dialog">
      <div className="bg-hbs-surface p-6 rounded max-w-md w-full max-h-[80vh] overflow-y-auto">
        <h2 className="text-lg font-semibold text-hbs-text mb-2">Add a recovery contact</h2>
        <p className="text-sm text-hbs-text-mute mb-4">
          Pick a family member who can help you recover your safe. Your safe key and account
          key are encrypted to them — they accept the role on their end and become able to
          help you if you forget your password.
        </p>
        <StepHeading n={1}>Choose a family member</StepHeading>
        {loadingMembers ? (
          <p className="text-sm text-hbs-text-mute py-8 text-center">Loading family members…</p>
        ) : candidates.length === 0 ? (
          <p className="text-sm text-hbs-text-mute py-8 text-center">
            No eligible family members. Invite someone to a family first.
          </p>
        ) : (
          <ul className="flex flex-col gap-1 mb-4">
            {candidates.map((c) => (
              <li key={c.userId}>
                <button
                  type="button"
                  id={`recovery-candidate-${c.userId}`}
                onClick={() => setSelectedUserId(c.userId)}
                  className={`w-full text-left p-3 rounded border ${
                    selectedUserId === c.userId
                      ? 'border-hbs-primary bg-hbs-primary/10'
                      : 'border-hbs-border bg-hbs-surface-alt hover:bg-hbs-surface'
                  }`}
                >
                  <div className="text-sm font-medium text-hbs-text">{c.email}</div>
                  <div className="text-xs text-hbs-text-mute">{c.familyName}</div>
                </button>
              </li>
            ))}
          </ul>
        )}
        {selected && (
          <div className="mt-4 mb-4 p-3 rounded border border-hbs-border bg-hbs-surface-alt">
            <StepHeading n={2}>Verify {selected.email}</StepHeading>
            {!selected.identityPublicKey || !selected.accountPublicKeySig ? (
              <p className="text-xs text-hbs-text-mute">
                This contact hasn't set up security verification yet. Ask them to sign in to the
                latest version of Hey Be Safe, then try again.
              </p>
            ) : selectedVerified ? (
              <p className="text-xs text-emerald-400" data-testid="contact-verified">
                ✓ Verified. You can add {selected.email} as a recovery contact.
              </p>
            ) : (
              <>
                <p className="text-xs text-hbs-text-mute mb-2">
                  To be sure no one is impersonating them, compare these six words with
                  {' '}{selected.email}{' '}
                  <strong className="font-semibold text-hbs-text">in person or on a call</strong>.
                  Ask them to open Hey Be Safe → Recovery contacts →{' '}
                  <em>Compare security words</em> and pick your name — they should see the
                  same six words in the same order.
                </p>
                <SafetyWordList words={safetyWordList} testId="safety-number" />
                <button
                  type="button"
                  id="recovery-verify-contact-button"
                  disabled={!safetyWordList}
                  onClick={handleVerify}
                  className="px-3 py-1.5 rounded text-sm border border-hbs-primary bg-hbs-primary/10 text-hbs-text disabled:opacity-50"
                >
                  It matches — verify {selected.email}
                </button>
              </>
            )}
          </div>
        )}
        {/* Step 3. Hidden until the contact is verified: showing all three
            controls at once read as "fill in this form", when it is really a
            sequence — pick someone, verify them out-of-band, then choose the
            window. The window is also meaningless for a contact who never
            clears verification, since Confirm stays disabled anyway. */}
        {selectedVerified && (
        <div className="mt-4 mb-4">
          <StepHeading n={3}>Choose a cancel window</StepHeading>
          <p className="text-xs text-hbs-text-mute mb-2">
            After they request emergency access, you have this long to cancel before
            they can read your safe. Shorter = more responsive in a real emergency;
            longer = more time for you to notice and stop a malicious request.
          </p>
          <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Cancel window">
            {EMERGENCY_WAIT_PRESETS_HOURS.map((h) => {
              const selected = h === waitHours;
              return (
                <button
                  type="button"
                  key={h}
                  role="radio"
                  aria-checked={selected}
                  id={`recovery-wait-${h}h`}
                  onClick={() => setWaitHours(h)}
                  className={`px-3 py-1.5 rounded text-sm border transition-colors ${
                    selected
                      ? 'border-hbs-primary bg-hbs-primary/10 text-hbs-text'
                      : 'border-hbs-border bg-hbs-surface-alt text-hbs-text-mute hover:bg-hbs-surface'
                  }`}
                >
                  {formatWaitPeriod(h)}
                </button>
              );
            })}
          </div>
        </div>
        )}
        {error && <p className="text-sm text-red-400 mb-2">{error}</p>}
        <div className="flex gap-2 justify-end">
          <button type="button" id="add-recovery-contact-cancel-button"
            onClick={onClose} className="px-3 py-2 text-sm text-hbs-text-mute">
            Cancel
          </button>
          <button
            type="button"
            disabled={!selectedUserId || !selectedVerified || submitting}
            id="add-recovery-contact-confirm-button"
            onClick={handleConfirm}
            className="bg-hbs-primary text-hbs-on-primary px-3 py-2 rounded font-semibold text-sm disabled:opacity-50"
          >
            {submitting ? 'Designating…' : 'Confirm'}
          </button>
        </div>
      </div>
    </div>
  );
}
